import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/App';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Search, 
  Filter, 
  MapPin, 
  Clock, 
  BarChart3,
  FileText,
  ArrowRight,
  Home,
  Train,
  ShoppingBag,
  Building2,
  Leaf,
  Store
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// NYC Policy Categories with icons
const NYC_CATEGORIES = [
  { id: 'housing', label: 'Housing', labelEs: 'Vivienda', icon: Home },
  { id: 'transportation', label: 'Transportation', labelEs: 'Transporte', icon: Train },
  { id: 'food_access', label: 'Food Access', labelEs: 'Acceso a Alimentos', icon: ShoppingBag },
  { id: 'small_business', label: 'Small Business', labelEs: 'Pequeñas Empresas', icon: Store },
  { id: 'development', label: 'Development', labelEs: 'Desarrollo', icon: Building2 },
  { id: 'environmental', label: 'Environmental', labelEs: 'Ambiental', icon: Leaf }
];

// NYC Boroughs
const NYC_BOROUGHS = ['Bronx', 'Brooklyn', 'Manhattan', 'Queens', 'Staten Island'];

const Policies = () => {
  const { language } = useLanguage();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [jurisdiction, setJurisdiction] = useState('all');
  const [category, setCategory] = useState('all');
  const [borough, setBorough] = useState('all');

  useEffect(() => {
    fetchPolicies();
  }, [jurisdiction, category, borough]);

  const fetchPolicies = async () => {
    try {
      const params = new URLSearchParams();
      if (jurisdiction !== 'all') params.append('jurisdiction', jurisdiction);
      if (category !== 'all') params.append('category', category);
      if (borough !== 'all') params.append('borough', borough);
      if (search) params.append('search', search);
      
      const response = await axios.get(`${API}/policies?${params.toString()}`);
      setPolicies(response.data);
    } catch (error) {
      toast.error(language === 'es' ? 'Error al cargar políticas' : 'Failed to load policies');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setLoading(true);
    fetchPolicies();
  };

  const getRiskBadge = (score) => {
    if (score >= 70) return <Badge className="risk-high">{language === 'es' ? 'Alto' : 'High'}</Badge>;
    if (score >= 40) return <Badge className="risk-medium">{language === 'es' ? 'Medio' : 'Medium'}</Badge>;
    return <Badge className="risk-low">{language === 'es' ? 'Bajo' : 'Low'}</Badge>;
  };

  const getJurisdictionLabel = (jur) => {
    const labels = {
      federal: language === 'es' ? 'Federal' : 'Federal',
      state: language === 'es' ? 'Estatal' : 'State',
      local: language === 'es' ? 'Local' : 'Local'
    };
    return labels[jur] || jur;
  };

  const getCategoryLabel = (cat) => {
    const categoryObj = NYC_CATEGORIES.find(c => c.id === cat);
    if (categoryObj) {
      return language === 'es' ? categoryObj.labelEs : categoryObj.label;
    }
    // Legacy fallback
    const labels = {
      environmental: language === 'es' ? 'Ambiental' : 'Environmental',
      voting: language === 'es' ? 'Votación' : 'Voting',
      housing: language === 'es' ? 'Vivienda' : 'Housing',
      immigration: language === 'es' ? 'Inmigración' : 'Immigration',
      reproductive: language === 'es' ? 'Reproductivo' : 'Reproductive',
      labor: language === 'es' ? 'Laboral' : 'Labor',
      health: language === 'es' ? 'Salud' : 'Health'
    };
    return labels[cat] || cat;
  };

  const getCategoryIcon = (cat) => {
    const categoryObj = NYC_CATEGORIES.find(c => c.id === cat);
    return categoryObj ? categoryObj.icon : FileText;
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="policies-loading">
        <Skeleton className="h-10 w-64" />
        <div className="flex gap-4">
          <Skeleton className="h-10 flex-1" />
          <Skeleton className="h-10 w-40" />
          <Skeleton className="h-10 w-40" />
        </div>
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-32" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="policies-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
          {language === 'es' ? 'Navegador de Políticas NYC' : 'NYC Policy Browser'}
        </h1>
        <p className="text-muted-foreground mt-1">
          {language === 'es' 
            ? 'Busca políticas de vivienda, transporte, acceso a alimentos y más'
            : 'Search housing, transportation, food access, and more policies'}
        </p>
      </div>

      {/* Category Quick Filters */}
      <div className="flex flex-wrap gap-2">
        {NYC_CATEGORIES.map((cat) => {
          const Icon = cat.icon;
          const isActive = category === cat.id;
          return (
            <Button
              key={cat.id}
              variant={isActive ? "default" : "outline"}
              size="sm"
              onClick={() => setCategory(isActive ? 'all' : cat.id)}
              className="gap-2"
              data-testid={`category-quick-${cat.id}`}
            >
              <Icon className="h-4 w-4" />
              {language === 'es' ? cat.labelEs : cat.label}
            </Button>
          );
        })}
      </div>

      {/* Filters */}
      <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={language === 'es' ? 'Buscar políticas...' : 'Search policies...'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
        
        <Select value={borough} onValueChange={setBorough}>
          <SelectTrigger className="w-full sm:w-[160px]" data-testid="borough-filter">
            <MapPin className="h-4 w-4 mr-2" />
            <SelectValue placeholder={language === 'es' ? 'Distrito' : 'Borough'} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{language === 'es' ? 'Todos' : 'All Boroughs'}</SelectItem>
            {NYC_BOROUGHS.map(b => (
              <SelectItem key={b} value={b}>{b}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select value={jurisdiction} onValueChange={setJurisdiction}>
          <SelectTrigger className="w-full sm:w-[160px]" data-testid="jurisdiction-filter">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder={language === 'es' ? 'Jurisdicción' : 'Jurisdiction'} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{language === 'es' ? 'Todas' : 'All'}</SelectItem>
            <SelectItem value="federal">{language === 'es' ? 'Federal' : 'Federal'}</SelectItem>
            <SelectItem value="state">{language === 'es' ? 'Estatal (NY)' : 'State (NY)'}</SelectItem>
            <SelectItem value="local">{language === 'es' ? 'Local (NYC)' : 'Local (NYC)'}</SelectItem>
          </SelectContent>
        </Select>
        
        <Button type="submit" data-testid="search-btn">
          <Search className="h-4 w-4 mr-2" />
          {language === 'es' ? 'Buscar' : 'Search'}
        </Button>
      </form>

      {/* Results Count */}
      <div className="text-sm text-muted-foreground">
        {policies.length} {language === 'es' ? 'políticas encontradas' : 'policies found'}
      </div>

      {/* Policy List */}
      <div className="space-y-4">
        {policies.length > 0 ? (
          policies.map((policy, index) => (
            <Card 
              key={policy.id} 
              className="transition-all hover:shadow-lg hover:border-primary/20"
              data-testid={`policy-card-${index}`}
            >
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                  <div className={`w-1.5 h-full min-h-[80px] rounded-full hidden lg:block ${
                    policy.risk_score >= 70 ? 'bg-accent' : 
                    policy.risk_score >= 40 ? 'bg-warning' : 'bg-success'
                  }`} />
                  
                  <div className="flex-1">
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                      <div>
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <Badge variant="outline" className="flex items-center gap-1">
                            {React.createElement(getCategoryIcon(policy.category), { className: "h-3 w-3" })}
                            {getCategoryLabel(policy.category)}
                          </Badge>
                          <Badge variant="secondary">{getJurisdictionLabel(policy.jurisdiction)}</Badge>
                          {policy.state && <Badge variant="secondary">{policy.state}</Badge>}
                          {policy.borough && (
                            <Badge variant="outline" className="bg-primary/5">
                              <MapPin className="h-3 w-3 mr-1" />
                              {policy.borough}
                            </Badge>
                          )}
                        </div>
                        <h3 className="font-semibold text-lg">{policy.title}</h3>
                      </div>
                      {getRiskBadge(policy.risk_score)}
                    </div>
                    
                    <p className="text-muted-foreground mt-2 line-clamp-2">
                      {language === 'es' && policy.plain_language_summary_es 
                        ? policy.plain_language_summary_es 
                        : policy.plain_language_summary || policy.description}
                    </p>
                    
                    <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {policy.status === 'pending' 
                          ? (language === 'es' ? 'Pendiente' : 'Pending')
                          : (language === 'es' ? 'Activo' : 'Active')
                        }
                      </span>
                      {policy.effective_date && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {language === 'es' ? 'Efectivo' : 'Effective'}: {policy.effective_date}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <BarChart3 className="h-4 w-4" />
                        {language === 'es' ? 'Puntuación de Riesgo' : 'Risk Score'}: {policy.risk_score}
                      </span>
                    </div>
                    
                    <div className="flex gap-2 mt-4">
                      <Button asChild size="sm" data-testid={`view-policy-${index}`}>
                        <Link to={`/policies/${policy.id}`}>
                          {language === 'es' ? 'Ver Detalles' : 'View Details'}
                          <ArrowRight className="ml-1 h-4 w-4" />
                        </Link>
                      </Button>
                      <Button asChild size="sm" variant="outline" data-testid={`comment-policy-${index}`}>
                        <Link to={`/comments?policy=${policy.id}`}>
                          {language === 'es' ? 'Generar Comentario' : 'Generate Comment'}
                        </Link>
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="font-medium text-lg mb-2">
                {language === 'es' ? 'No se encontraron políticas' : 'No policies found'}
              </h3>
              <p className="text-muted-foreground">
                {language === 'es' 
                  ? 'Intenta ajustar tus filtros de búsqueda'
                  : 'Try adjusting your search filters'}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Policies;
